import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  AlertTriangle, Loader2, ChevronDown, ChevronUp, Trash2, RefreshCw, Check,
} from "lucide-react";

/**
 * Fuzzy-duplicate review panel — consumes GET /received-invoices/possible-duplicates
 * and lets the user keep one invoice per group while deleting the rest.
 *
 * Closed by default so it doesn't clutter the page when there are no
 * potential duplicates; a soft banner with the group count invites the user
 * to expand it.
 */
const fmtEur = (v) =>
  new Intl.NumberFormat("ca-ES", { style: "currency", currency: "EUR" })
    .format(Number(v) || 0);

export default function PossibleDuplicatesPanel({ onChange }) {
  const { lang } = useI18n();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // groupId → set of invoice IDs marked to KEEP (defaults to oldest one)
  const [keepers, setKeepers] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/received-invoices/possible-duplicates");
      setGroups(data.groups || []);
      // Initialize keepers — keep the FIRST (oldest) invoice in each group
      const k = {};
      (data.groups || []).forEach((g, idx) => {
        const groupId = `${g.key}-${idx}`;
        k[groupId] = g.invoices[0]?.id || null;
      });
      setKeepers(k);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalDuplicates = groups.reduce(
    (s, g) => s + Math.max(0, g.count - 1), 0);

  if (loading && groups.length === 0) {
    return null;
  }
  if (groups.length === 0) {
    return null;
  }

  const resolveGroup = async (group, groupId) => {
    const keepId = keepers[groupId];
    if (!keepId) return;
    const toDelete = group.invoices
      .map((i) => i.id)
      .filter((id) => id !== keepId);
    if (toDelete.length === 0) {
      toast.info(lang === "es" ? "Nada a eliminar" : "Res a eliminar");
      return;
    }
    if (!window.confirm(
      lang === "es"
        ? `Eliminar ${toDelete.length} factura(s) y mantener solo 1 del grupo "${group.supplier_name}"?`
        : `Eliminar ${toDelete.length} factura(es) i mantenir-ne només 1 del grup "${group.supplier_name}"?`
    )) return;
    try {
      const { data } = await api.post("/received-invoices/bulk-delete", {
        ids: toDelete,
      });
      toast.success(
        lang === "es"
          ? `${data.deleted_count} eliminadas`
          : `${data.deleted_count} eliminades`
      );
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div
      className="card-surface border-amber-500/30 bg-amber-500/[0.04]"
      data-testid="possible-duplicates-panel"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition-colors"
        data-testid="possible-duplicates-toggle"
      >
        <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
          <AlertTriangle size={18} className="text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">
            {lang === "es"
              ? `Posibles duplicados detectados (${groups.length} grupos · ${totalDuplicates} repetidas)`
              : `Possibles duplicats detectats (${groups.length} grups · ${totalDuplicates} repetides)`}
          </div>
          <div className="text-xs text-slate-400">
            {lang === "es"
              ? "Mismo proveedor + total ±5% + fecha ±7d. Revisa y mantén solo una."
              : "Mateix proveïdor + total ±5% + data ±7d. Revisa i mantén-ne només una."}
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); load(); }}
          className="p-2 rounded hover:bg-white/[0.05] text-slate-300"
          title={lang === "es" ? "Recargar" : "Recarregar"}
          data-testid="possible-duplicates-reload"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </button>
        {expanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-amber-500/20">
          {groups.map((group, gIdx) => {
            const groupId = `${group.key}-${gIdx}`;
            const keepId = keepers[groupId];
            return (
              <div
                key={groupId}
                className="rounded-lg border border-white/[0.08] bg-slate-900/40 p-3"
                data-testid={`dup-group-${gIdx}`}
              >
                <div className="flex flex-wrap items-center gap-2 justify-between mb-2">
                  <div className="text-sm">
                    <span className="font-semibold text-white">{group.supplier_name || "—"}</span>
                    {group.supplier_nif && (
                      <span className="font-mono text-xs text-slate-400 ml-2">{group.supplier_nif}</span>
                    )}
                    <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-bold">
                      {group.count}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => resolveGroup(group, groupId)}
                    disabled={!keepId}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid={`dup-resolve-${gIdx}`}
                  >
                    <Trash2 size={12} className="mr-1.5" />
                    {lang === "es"
                      ? `Eliminar ${group.count - 1}`
                      : `Eliminar ${group.count - 1}`}
                  </Button>
                </div>
                <div className="space-y-1">
                  {group.invoices.map((inv) => {
                    const isKeep = keepId === inv.id;
                    return (
                      <button
                        key={inv.id}
                        type="button"
                        onClick={() =>
                          setKeepers((k) => ({ ...k, [groupId]: inv.id }))
                        }
                        className={`w-full flex items-center gap-2 p-2 rounded text-left transition-colors ${
                          isKeep
                            ? "bg-emerald-500/10 border border-emerald-500/40"
                            : "bg-white/[0.02] border border-transparent hover:bg-white/[0.05]"
                        }`}
                        data-testid={`dup-row-${inv.id}`}
                      >
                        <div className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${
                          isKeep ? "bg-emerald-500 text-white" : "bg-slate-700"
                        }`}>
                          {isKeep && <Check size={10} />}
                        </div>
                        <div className="flex-1 text-xs grid grid-cols-1 sm:grid-cols-4 gap-1 sm:gap-3">
                          <div className="text-slate-300">{inv.issue_date || "—"}</div>
                          <div className="text-slate-300 font-mono truncate">{inv.document_number || "—"}</div>
                          <div className="text-white font-mono font-semibold text-right sm:text-left">
                            {fmtEur(inv.total)}
                          </div>
                          <div className="text-slate-400 text-[10px] truncate">
                            {inv.file_name}
                          </div>
                        </div>
                        {isKeep && (
                          <span className="text-[10px] font-bold text-emerald-300 uppercase tracking-wider">
                            {lang === "es" ? "Mantener" : "Mantenir"}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
